/**
 * Boot tracker for the page loader.
 *
 * Every asset the first frame depends on (fonts, the WebGL scenes, the portrait
 * texture) is declared up front and resolved when ready, so the loader can show
 * real progress and only lift once the animations are actually able to run.
 */
export type BootTask = 'fonts' | 'backdrop' | 'portrait';

const TASKS: BootTask[] = ['fonts', 'backdrop', 'portrait'];
const done = new Set<BootTask>();
const listeners = new Set<(progress: number) => void>();

/** Safety net: never trap the visitor behind the loader. */
const MAX_WAIT_MS = 7000;

const progress = () => done.size / TASKS.length;

export function resolveTask(task: BootTask) {
  if (done.has(task)) return;
  done.add(task);
  const p = progress();
  listeners.forEach((fn) => fn(p));
}

export function onBootProgress(fn: (progress: number) => void) {
  listeners.add(fn);
  fn(progress());
  return () => {
    listeners.delete(fn);
  };
}

if (typeof window !== 'undefined') {
  document.fonts?.ready.then(() => resolveTask('fonts'));
  window.setTimeout(() => TASKS.forEach(resolveTask), MAX_WAIT_MS);
}
